// src/pages/product-module/UpdateProduct.js

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardWrapper from "../../components/DashboardWrapper";
import CrudForm from "../../components/dashboard/form/CrudForm";
import productService from "../../services/productService";

export default function UpdateProduct() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [brands, setBrands] = useState([]);
  const [initialData, setInitialData] = useState(null);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);

  const [loading, setLoading] = useState(true);

  const localCacheKey = `productEditCache_${id}`;

  // ============================================================
  // FIELDS ALLOWED IN PRODUCT FORM
  // ============================================================

  const allowedFields = [
    "name",
    // "thumb_image",
    "images",

    "category_id",
    "sub_category_id",
    "child_category_id",

    // "brand_id",

    "qty",

    "short_description",
    "long_description",

    "video_link",

    "sku",

    "price",
    "offer_price",

    "offer_start_date",
    "offer_end_date",

    "product_type",

    // "status",

    // "is_prescription",

    "seo_title",
    "seo_description",

    "is_approved",

    "colors",
  ];

  // ============================================================
  // LOAD PRODUCT + DROPDOWNS
  // ============================================================

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);

        // ======================================================
        // 1. PRODUCT IS REQUIRED
        // ======================================================

        let productRes;

        try {
          productRes = await productService.getProductById(id);

          console.log("GET PRODUCT RESPONSE:", productRes);
        } catch (productError) {
          console.error(
            "GET PRODUCT ERROR:",
            productError?.response?.data || productError,
          );

          throw productError;
        }

        if (!productRes) {
          throw new Error("Product response is empty");
        }

        // ======================================================
        // 2. EXTRACT PRODUCT DATA SAFELY
        // ======================================================

        const product =
          productRes?.data?.data || productRes?.data || productRes;

        console.log("PRODUCT DATA FOR EDIT:", product);

        if (!product || !product.id) {
          console.error("Invalid product response:", product);

          throw new Error("Product data not found");
        }

        // ======================================================
        // 3. NORMALIZE PRODUCT DATA
        // ======================================================

        const normalized = allowedFields.reduce((acc, field) => {
          if (
            field === "status" ||
            field === "is_prescription" ||
            field === "is_approved"
          ) {
            acc[field] = product[field] ?? 0;
          } else {
            acc[field] = product[field] ?? "";
          }

          return acc;
        }, {});

        // ======================================================
        // 4. NORMALIZE COLORS
        // ======================================================

        normalized.colors = Array.isArray(product.colors)
          ? product.colors
              .map((color) => {
                if (typeof color === "string") {
                  return color;
                }

                return color?.color_code || color?.color || "";
              })
              .filter(Boolean)
          : [];

        // ======================================================
        // 5. NORMALIZE THUMBNAIL
        // ======================================================

        const thumbnail = product.thumb_image
          ? [
              {
                image: product.thumb_image,
                type: "thumb",
              },
            ]
          : [];

        // ======================================================
        // 6. NORMALIZE GALLERY IMAGES
        // ======================================================

        const galleryImages = Array.isArray(product.product_image_galleries)
          ? product.product_image_galleries
              .filter((img) => img?.image)
              .map((img) => ({
                id: img.id,
                image: img.image,
                type: "gallery",
              }))
          : [];

        // ======================================================
        // 7. COMBINE THUMB + GALLERY
        // ======================================================

        normalized.images = [...thumbnail, ...galleryImages];

        console.log("NORMALIZED EDIT DATA:", normalized);

        // ======================================================
        // 8. SET PRODUCT DATA IMMEDIATELY
        // ======================================================

        if (!isMounted) return;

        setInitialData(normalized);

        // ======================================================
        // 9. CACHE PRODUCT DATA
        // ======================================================

        try {
          localStorage.setItem(localCacheKey, JSON.stringify(normalized));
        } catch (cacheError) {
          console.warn("Unable to cache product:", cacheError);
        }

        // ======================================================
        // 10. LOAD DROPDOWNS SEPARATELY
        //
        // IMPORTANT:
        // Dropdown failure should NOT stop product editing.
        // ======================================================

        const [catResult, subCatResult, childCatResult, brandResult] =
          await Promise.allSettled([
            productService.getAllCategories(),
            productService.getAllSubCategories(),
            productService.getAllChildCategories(),
            productService.getAllBrands(),
          ]);

        // ======================================================
        // CATEGORIES
        // ======================================================

        if (catResult.status === "fulfilled") {
          const categoryData =
            catResult.value?.data?.data || catResult.value?.data || [];

          setCategories(Array.isArray(categoryData) ? categoryData : []);
        } else {
          console.warn(
            "Categories API failed:",
            catResult.reason?.response?.data || catResult.reason,
          );

          setCategories([]);
        }

        // ======================================================
        // SUB CATEGORIES
        // ======================================================

        if (subCatResult.status === "fulfilled") {
          const subCategoryData =
            subCatResult.value?.data?.data || subCatResult.value?.data || [];

          setSubCategories(
            Array.isArray(subCategoryData) ? subCategoryData : [],
          );
        } else {
          console.warn(
            "Sub categories API failed:",
            subCatResult.reason?.response?.data || subCatResult.reason,
          );

          setSubCategories([]);
        }

        // ======================================================
        // CHILD CATEGORIES
        // ======================================================

        if (childCatResult.status === "fulfilled") {
          const childCategoryData =
            childCatResult.value?.data?.data ||
            childCatResult.value?.data ||
            [];

          setChildCategories(
            Array.isArray(childCategoryData) ? childCategoryData : [],
          );
        } else {
          console.warn(
            "Child categories API failed:",
            childCatResult.reason?.response?.data || childCatResult.reason,
          );

          setChildCategories([]);
        }

        // ======================================================
        // BRANDS
        // ======================================================

        if (brandResult.status === "fulfilled") {
          let brandsData =
            brandResult.value?.data?.data || brandResult.value?.data || [];

          if (!Array.isArray(brandsData)) {
            brandsData = [];
          }

          const isActive = (item) =>
            item?.status === true ||
            item?.status === 1 ||
            item?.status === "1" ||
            item?.status === "true";

          brandsData = brandsData.filter(isActive);

          setBrands(brandsData);
        } else {
          console.warn(
            "Brands API failed:",
            brandResult.reason?.response?.data || brandResult.reason,
          );

          setBrands([]);
        }
      } catch (error) {
        console.error(
          "Failed to load product:",
          error?.response?.data || error,
        );

        if (!isMounted) return;

        Swal.fire({
          title: "Error",
          text:
            error?.response?.data?.message || "Failed to load product details.",
          icon: "error",
          confirmButtonColor: "#FB6C22",
        });

        navigate("/admin/all-products");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // ==========================================================
    // LOAD CACHE FIRST
    // ==========================================================

    try {
      const cached = localStorage.getItem(localCacheKey);

      if (cached) {
        const cachedData = JSON.parse(cached);

        if (cachedData && cachedData.name) {
          console.log("USING CACHED PRODUCT DATA:", cachedData);

          setInitialData(cachedData);
          setLoading(false);
        }
      }
    } catch (cacheError) {
      console.warn("Invalid product cache:", cacheError);

      localStorage.removeItem(localCacheKey);
    }

    // ==========================================================
    // ALWAYS GET LATEST PRODUCT FROM API
    // ==========================================================

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, navigate, localCacheKey]);

  // ============================================================
  // HANDLE SUBMIT
  // ============================================================

  const handleSubmit = useCallback(
    async (formData) => {
      console.log("FORM DATA BEFORE SUBMIT:", formData);

      console.log("IMAGES ARRAY:", formData.images);

      try {
        // ======================================================
        // CREATE SLUG
        // ======================================================

        const slug =
          formData.name?.toLowerCase().trim().replace(/\s+/g, "-") || "";

        // ======================================================
        // PAYLOAD
        // ======================================================

        const payload = {
          ...formData,

          slug,

          status: Number(formData.status ?? 1),

          is_prescription: Number(formData.is_prescription ?? 1),

          is_approved: Number(formData.is_approved ?? 0),
        };

        // Images are handled separately
        delete payload.images;

        // ======================================================
        // CREATE FORM DATA
        // ======================================================

        const form = new FormData();

        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            form.append(key, value.toString());
          }
        });

        // Laravel method override
        form.append("_method", "PUT");

        // ======================================================
        // IMAGES
        // ======================================================

        const imagesArray = Array.isArray(formData.images)
          ? formData.images
          : [];

        imagesArray.forEach((img) => {
          // ====================================================
          // NEW THUMBNAIL
          // ====================================================

          if (img?.type === "thumb" && img?.file instanceof File) {
            form.append("thumb_image", img.file);

            return;
          }

          // ====================================================
          // NEW GALLERY IMAGE
          // ====================================================

          if (img instanceof File) {
            form.append("images[]", img);

            return;
          }

          // ====================================================
          // EXISTING GALLERY IMAGE
          // ====================================================

          if (img?.image && img?.type !== "thumb") {
            form.append("existing_images[]", img.image);
          }
        });

        // ======================================================
        // COLORS
        // ======================================================

        const colorsArray = Array.isArray(formData.colors)
          ? formData.colors
          : [];

        colorsArray.forEach((color) => {
          if (color) {
            form.append("colors[]", color);
          }
        });

        // ======================================================
        // DEBUG FORM DATA
        // ======================================================

        console.log("========== UPDATE FORM DATA ==========");

        for (const pair of form.entries()) {
          console.log(pair[0], pair[1]);
        }

        console.log("=======================================");

        // ======================================================
        // UPDATE API
        // ======================================================

        const res = await productService.updateProductWithFile(id, form);

        console.log("UPDATE RESPONSE:", res);

        // ======================================================
        // CLEAR CACHE AFTER SUCCESS
        // ======================================================

        localStorage.removeItem(localCacheKey);

        // ======================================================
        // SUCCESS MESSAGE
        // ======================================================

        await Swal.fire({
          title: "Success",
          text: "Product updated successfully!",
          icon: "success",
          showConfirmButton: true,
          confirmButtonText: "OK",
          confirmButtonColor: "#FB6C22",
          customClass: {
            confirmButton: "swal-confirm-btn",
          },
          didOpen: () => {
            if (!document.getElementById("swal-button-style")) {
              const style = document.createElement("style");

              style.id = "swal-button-style";

              style.innerHTML = `
                .swal-confirm-btn {
                  background-color: var(--color-text-green) !important;
                  color: #fff !important;
                  border: none !important;
                  border-radius: 4px !important;
                  padding: 8px 20px !important;
                  font-weight: bold !important;
                }
              `;

              document.head.appendChild(style);
            }
          },
        });

        // ======================================================
        // REDIRECT
        // ======================================================

        navigate("/admin/all-products");
      } catch (error) {
        console.error("UPDATE PRODUCT ERROR:", error?.response?.data || error);

        const errors = error?.response?.data?.errors || {};

        const firstError =
          Object.values(errors)[0]?.[0] ||
          error?.response?.data?.message ||
          "Update failed";

        Swal.fire({
          title: "Error",
          text: firstError,
          icon: "error",
          confirmButtonColor: "#FB6C22",
        });
      }
    },
    [id, navigate, localCacheKey],
  );

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <DashboardWrapper>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            minHeight: "300px",
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardWrapper>
    );
  }

  // ============================================================
  // NO PRODUCT
  // ============================================================

  if (!initialData) {
    return (
      <DashboardWrapper>
        <div className="alert alert-danger">Product data not found.</div>
      </DashboardWrapper>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <DashboardWrapper>
      <div className="p-4">
        <h1 className="h4 mb-4">Update Product</h1>

        <div className="card">
          <h5 className="card-header">Edit Product</h5>

          <div className="card-body">
            <CrudForm
              initialData={initialData}
              onSubmit={handleSubmit}
              allowedFields={allowedFields}
              fileUploadStyle="product"
              fileFields={[
                {
                  name: "images",
                  multiple: true,
                },
              ]}
              categories={categories}
              subCategories={subCategories}
              childCategories={childCategories}
              brands={brands}
            />
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
